import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

function InsuranceSection() {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d76c4d4bmgf2o8mm74m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-4 sm:mb-6 rounded-2xl border-2 border-dashed border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
      {/* Badge + Header */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4">
        <div className="flex items-center gap-2 sm:gap-3 mb-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
            <i className="ri-shield-check-line text-amber-600 text-lg sm:text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm sm:text-base font-bold text-gray-900">Insurance &amp; Coverage</h4>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                <i className="ri-time-line text-xs"></i>
                Upcoming Feature
              </span>
            </div>
            <p className="text-xs text-amber-700 font-medium mt-0.5">Currently being integrated</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          We are currently integrating insurance coverage details, including cashless facilities and claim estimates, for each hospital.
        </p>

        {/* Benefits list */}
        <ul className="space-y-2 mb-4">
          {[
            { icon: 'ri-check-double-line', text: 'Check which hospitals accept your insurance' },
            { icon: 'ri-money-rupee-circle-line', text: 'Understand estimated coverage and out-of-pocket costs' },
            { icon: 'ri-file-list-3-line', text: 'Get simple guidance for the claim process' },
          ].map((item) => (
            <li key={item.text} className="flex items-start gap-2">
              <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className={`${item.icon} text-amber-600 text-sm`}></i>
              </span>
              <span className="text-sm text-gray-700">{item.text}</span>
            </li>
          ))}
        </ul>

        <p className="text-xs text-gray-500 italic mb-4">
          Launching soon to make your healthcare decisions smarter and more transparent.
        </p>

        {/* CTA Buttons */}
        {!showForm && !submitted && (
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowForm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap"
            >
              <i className="ri-notification-3-line"></i>
              Notify Me
            </button>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-amber-400 text-amber-700 text-sm font-semibold rounded-xl hover:bg-amber-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Contact Us
            </a>
          </div>
        )}

        {/* Notify Me Form */}
        {showForm && !submitted && (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-1"
          >
            <p className="text-xs text-gray-600 mb-2 font-medium">Enter your email to get notified when this feature launches:</p>
            <div className="flex gap-2">
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-3 py-2.5 border-2 border-amber-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 bg-white"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  'Notify'
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Cancel
            </button>
          </form>
        )}

        {/* Success State */}
        {submitted && (
          <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>
            <p className="text-sm text-emerald-700 font-medium">
              You&apos;re on the list! We&apos;ll notify you when insurance coverage launches.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface Facility {
  id: number;
  name: string;
  type: 'Hospital' | 'Diagnostic Center';
  location: string;
  minCost: number;
  maxCost: number | null;
  services: string;
  category: 'Affordable' | 'Mid-Range' | 'Premium';
  rating: number;
  waitTime: number;
  image: string;
  bedCount?: number;
  doctors?: number;
  phone?: string;
}

const facilities: Facility[] = [
  // Previous 21 Hospitals
  {
    id: 1,
    name: 'Jehangir Hospital',
    type: 'Hospital',
    location: 'Jehangir Hospital, 32, Sasoon Rd, opposite Railway Station, Central Excise Colony, Sangamvadi, Pune, Maharashtra 411001',
    minCost: 50000,
    maxCost: 200000,
    services: 'Gynecology, Neurology, Cardiac, Renal',
    category: 'Premium',
    rating: 4.8,
    waitTime: 30,
    bedCount: 350,
    doctors: 150,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/9e864d72-19f1-41e5-a42e-5bbfc7fd7851_jh.png?v=3c9f7f020d78e197d40c821b5786b5fb'
  },
  {
    id: 2,
    name: 'Gupte Hospital',
    type: 'Hospital',
    location: 'Bhandarkar Road, Pune',
    minCost: 30000,
    maxCost: 150000,
    services: 'Pregnancy Care, Neonatal Care, Fertility',
    category: 'Mid-Range',
    rating: 4.6,
    waitTime: 20,
    bedCount: 80,
    doctors: 40,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/ce7bdbfc-ca91-4800-bcef-6b68e6e7c3f2_gupte-hos-gallery2.jpg?v=5b8a65e1ddfe74d222d76737a86317ec'
  },
  {
    id: 3,
    name: 'Deenanath Mangeshkar Hospital & Research Center',
    type: 'Hospital',
    location: 'Erandwane, Pune',
    minCost: 70000,
    maxCost: 300000,
    services: 'Multispecialty, Neonatal, Gynecology',
    category: 'Premium',
    rating: 4.9,
    waitTime: 40,
    bedCount: 900,
    doctors: 250,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/734a5b38-e417-4613-b040-16b4289cdfc2_Deenanath-Mangeshkar-Hospital-and-Research-Center_pune-1.webp?v=7117f3ec7799a7098b88aac61b2d3ef2'
  },
  {
    id: 4,
    name: 'Yashwantrao Chavan Memorial Hospital',
    type: 'Hospital',
    location: 'Pimpri, Pune',
    minCost: 15000,
    maxCost: 80000,
    services: 'Obstetrics, Gynecology, Pediatrics',
    category: 'Affordable',
    rating: 4.2,
    waitTime: 45,
    bedCount: 750,
    doctors: 100,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/31db0662-7c7f-41aa-9ae5-ba7396846102_WhatsApp-Image-2023-12-13-at-11.39.02-AM.jpg?v=087e5b802108bf70144a1065a291c1c5'
  },
  {
    id: 5,
    name: 'Kamala Nehru Hospital',
    type: 'Hospital',
    location: 'Mangalwar Peth, Pune',
    minCost: 10000,
    maxCost: 60000,
    services: 'Maternity, Child Health, Emergency Services',
    category: 'Affordable',
    rating: 4.1,
    waitTime: 30,
    bedCount: 200,
    doctors: 50,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/24475095-5cba-4c97-9afc-744529fdfd5d_PAGE-6-TOPPER.webp?v=9398fe7e5f42189a0622c3a6223fcf9d'
  },
  {
    id: 6,
    name: 'Cloudnine Hospital',
    type: 'Hospital',
    location: 'SB Road, Pune',
    minCost: 60000,
    maxCost: 200000,
    services: 'Maternity, Neonatology, Fertility, Pediatrics',
    category: 'Premium',
    rating: 4.8,
    waitTime: 25,
    bedCount: 80,
    doctors: 40,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/34e514b4-7e59-441e-a148-59cf2b272d18_6808b121674bf69a6db36a80_TNagar-centre.jpg?v=7f1778df40448cd711ab35adb9700fde'
  },
  {
    id: 7,
    name: 'Motherhood Hospital',
    type: 'Hospital',
    location: 'Kharadi, Pune',
    minCost: 40000,
    maxCost: 180000,
    services: 'Maternity, Neonatology, Fertility Services',
    category: 'Premium',
    rating: 4.7,
    waitTime: 20,
    bedCount: 50,
    doctors: 30,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/43e2fedc-6df7-4f23-8623-d51ac5819523_Kharadi-Pune.jpg?v=89094658cdfdc55a53a8715575a0c4c1'
  },
  {
    id: 8,
    name: 'Cradle Maternity and Women Care',
    type: 'Hospital',
    location: 'Pimple Nilakh, Pimpri-Chinchwad',
    minCost: 50000,
    maxCost: 200000,
    services: 'Maternity, Obstetrics, Fertility Treatments',
    category: 'Premium',
    rating: 4.6,
    waitTime: 30,
    bedCount: 100,
    doctors: 50,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/b58e0836-061b-4f50-8493-90f455c36976_Screenshot-2026-04-07-153232.png?v=ee7eece217c8d566cc315abb6f410dc8'
  },
  {
    id: 9,
    name: 'Ankura Hospital for Women & Children',
    type: 'Hospital',
    location: 'Aundh, Pune',
    minCost: 70000,
    maxCost: 250000,
    services: 'Neonatology, Pediatrics, Obstetrics',
    category: 'Premium',
    rating: 4.8,
    waitTime: 30,
    bedCount: 150,
    doctors: 70,
    phone: '09053108108',
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/72dc617d-2522-4e85-b220-efae4b2a131b_OIZYbfoqN3gYXFroVqlcdVUlvM5g8sBVvPpO0Kzu.webp?v=c1ae15ab5d4f561a894bc2902206a2c0'
  },
  {
    id: 10,
    name: 'Dugad Hospital',
    type: 'Hospital',
    location: 'Maharshi Nagar, Pune',
    minCost: 30000,
    maxCost: 120000,
    services: 'Maternity, General Medicine',
    category: 'Mid-Range',
    rating: 4.3,
    waitTime: 25,
    bedCount: 50,
    doctors: 25,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/4c5c3525-6187-45a9-b01f-7316868d7498_unnamed-2025-03-28T221450.482.jpg?v=43cdf2dfa8cf5c864042c234448391dc'
  },
  {
    id: 11,
    name: 'Aditya Birla Memorial Hospital',
    type: 'Hospital',
    location: 'Chinchwad, Pune',
    minCost: 80000,
    maxCost: 300000,
    services: 'Multispecialty, Neonatal, Maternity',
    category: 'Premium',
    rating: 4.9,
    waitTime: 40,
    bedCount: 500,
    doctors: 250,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/06a3d6a0-084b-41d8-8b72-4121e3e1a713_why-choose-abmh.webp?v=27ce929bbb13563c9846ac9305c95209'
  },
  {
    id: 12,
    name: 'Ruby Hall Clinic',
    type: 'Hospital',
    location: 'Sassoon Road, Pune',
    minCost: 70000,
    maxCost: 350000,
    services: 'Neonatology, Obstetrics, Multispecialty',
    category: 'Premium',
    rating: 4.9,
    waitTime: 35,
    bedCount: 750,
    doctors: 300,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/62a3c046-7f6f-4271-adaa-03cd0f5906c3_gallery-3.jpg?v=6a53a26232508cdef10c3dba3a01d1f5'
  },
  {
    id: 13,
    name: 'Bharati Hospital',
    type: 'Hospital',
    location: 'Katraj, Pune',
    minCost: 30000,
    maxCost: 150000,
    services: 'Obstetrics, Pediatrics, General Medicine',
    category: 'Affordable',
    rating: 4.4,
    waitTime: 30,
    bedCount: 400,
    doctors: 200,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/e68c9962-57a8-43c4-8ac6-f85b64ca5638_2_Hospital_170523.jpg?v=e3c6f8b5a48856e7fb796ffed751a585'
  },
  {
    id: 14,
    name: 'Noble Hospital',
    type: 'Hospital',
    location: 'Hadapsar, Pune',
    minCost: 50000,
    maxCost: 250000,
    services: 'Neonatology, Obstetrics, Gynecology',
    category: 'Premium',
    rating: 4.7,
    waitTime: 25,
    bedCount: 250,
    doctors: 120,
    phone: '8007006611',
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/2e66ae11-5e30-4b5e-9f59-3d23489a5789_noble-hospital-magarpatta-city-hadapsar-pune-hospitals-84rl6zzwbi.jpg?v=2a90deb48678dfca41a818eda73a09fb'
  },
  {
    id: 15,
    name: 'Lotus Multispeciality Hospital',
    type: 'Hospital',
    location: 'Pimpale Saudagar, Pune',
    minCost: 40000,
    maxCost: 200000,
    services: 'Pediatrics, Obstetrics, Emergency Services',
    category: 'Mid-Range',
    rating: 4.5,
    waitTime: 30,
    bedCount: 120,
    doctors: 50,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/6887cdde-3798-4664-a239-39dd017732aa_lotus-multispeciality-hospital-pune-1469185796-5791ff041f639.jpg?v=ea8aa0126f224c3fb2d5fa8591ac8fb0'
  },
  {
    id: 16,
    name: 'Sahyadri Hospitals',
    type: 'Hospital',
    location: 'Multiple Locations, Pune',
    minCost: 60000,
    maxCost: 200000,
    services: 'Multispecialty, Neonatal, Maternity',
    category: 'Mid-Range',
    rating: 4.6,
    waitTime: 30,
    bedCount: 600,
    doctors: 300,
    phone: '08888822222',
    image: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/2922589b5c078820e2a16c2829df575e.png'
  },
  {
    id: 17,
    name: 'KEM Hospital',
    type: 'Hospital',
    location: 'Rasta Peth, Pune',
    minCost: 20000,
    maxCost: 100000,
    services: 'Multispecialty, Pediatrics, Obstetrics',
    category: 'Affordable',
    rating: 4.6,
    waitTime: 45,
    bedCount: 1000,
    doctors: 400,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/d6caf8ff-f9c6-4a5f-855e-2aed98b6fb4e_Screenshot-2026-04-07-161545.png?v=8c6f6f944b414fa4c68643edf5e45a29'
  },
  {
    id: 18,
    name: 'Poona Hospital & Research Centre',
    type: 'Hospital',
    location: 'Shivaji Nagar, Pune',
    minCost: 50000,
    maxCost: 200000,
    services: 'Multispecialty, Maternity, Pediatrics',
    category: 'Premium',
    rating: 4.8,
    waitTime: 35,
    bedCount: 500,
    doctors: 250,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/1b650d83-446f-4919-a980-e7230fb10bef_Screenshot-2026-04-07-162148.png?v=f8493b575167d4e7775ff810178f840d'
  },
  {
    id: 19,
    name: 'Ranka Hospital',
    type: 'Hospital',
    location: 'Camp, Pune',
    minCost: 40000,
    maxCost: 150000,
    services: 'Obstetrics, Pediatrics, Gynecology',
    category: 'Mid-Range',
    rating: 4.5,
    waitTime: 30,
    bedCount: 100,
    doctors: 40,
    phone: '7387601495',
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/6788d9b8-9b4f-4451-bc22-3c45739c3bf3_1697430384072-399279349.jpg?v=6f0fa208bd5ad3f63c87e7f64396fe5e'
  },
  {
    id: 20,
    name: 'Mother Teresa Charitable Trust Hospital',
    type: 'Hospital',
    location: 'Yerawada, Pune',
    minCost: 20000,
    maxCost: 80000,
    services: 'Maternity, Pediatrics, General Medicine',
    category: 'Affordable',
    rating: 4.7,
    waitTime: 30,
    bedCount: 200,
    doctors: 100,
    image: 'https://public.readdy.ai/ai/img_res/10f8dae2b234849105355080892cb942.jpg'
  },
  {
    id: 21,
    name: 'Niramaya Hospital',
    type: 'Hospital',
    location: 'Pimpri-Chinchwad',
    minCost: 40000,
    maxCost: 180000,
    services: 'Maternity, Pediatrics, Neonatology',
    category: 'Mid-Range',
    rating: 4.6,
    waitTime: 25,
    bedCount: 120,
    doctors: 60,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/02266d76-7e1b-4a2b-93b5-7f7e21aad772_1-1.png?v=ad6304fdab2dd2e82afa13e1e225e875'
  },
  // New 11 Diagnostic Centers
  {
    id: 23,
    name: 'Gupte Hospital',
    type: 'Diagnostic Center',
    location: 'Bhandarkar Road, Pune',
    minCost: 1800,
    maxCost: 3200,
    services: 'Specialized in gynecology and diagnostics',
    category: 'Mid-Range',
    rating: 4.6,
    waitTime: 25,
    image: 'https://readdy.ai/api/search-image?query=gynecology-diagnostic-center-specialized-testing-facility-modern-medical-laboratory-professional-healthcare-building-clean-architecture-urban-location-bright-natural-lighting&width=800&height=600&seq=gupte-diagnostic-d002&orientation=landscape'
  },
  {
    id: 24,
    name: 'Deenanath Mangeshkar Hospital & Research Center',
    type: 'Diagnostic Center',
    location: 'Erandwane, Pune',
    minCost: 2500,
    maxCost: 4000,
    services: 'Advanced diagnostic and research capabilities',
    category: 'Premium',
    rating: 4.9,
    waitTime: 35,
    image: 'https://readdy.ai/api/search-image?query=advanced-research-diagnostic-center-high-tech-testing-facility-modern-medical-laboratory-professional-healthcare-building-contemporary-design-urban-location-bright-welcoming-environment&width=800&height=600&seq=deenanath-diagnostic-d003&orientation=landscape'
  },
  {
    id: 25,
    name: 'AIMS Hospital',
    type: 'Diagnostic Center',
    location: 'Aundh, Pune',
    minCost: 800,
    maxCost: null,
    services: 'Affordable diagnostic services',
    category: 'Affordable',
    rating: 4.3,
    waitTime: 20,
    image: 'https://storage.readdy-site.link/project_files/3a6b3997-5789-4a94-b2bd-7ea678d057c8/d997f38c-8efe-4f18-9d92-5ab4a2048c1c_aims-piles-hospital-pune-hospitals-pspavs0c88.jpg?v=90ce9a9ce9ccb702d97c4cb9b172282a'
  },
  {
    id: 26,
    name: 'Ranka Hospital',
    type: 'Diagnostic Center',
    location: 'Camp, Pune',
    minCost: 1500,
    maxCost: 3500,
    services: 'Comprehensive healthcare services',
    category: 'Mid-Range',
    rating: 4.5,
    waitTime: 30,
    image: 'https://readdy.ai/api/search-image?query=comprehensive-diagnostic-center-full-service-testing-facility-modern-medical-laboratory-professional-healthcare-building-clean-design-urban-location-bright-natural-lighting&width=800&height=600&seq=ranka-diagnostic-d005&orientation=landscape'
  },
  {
    id: 27,
    name: 'Redcliffe Labs',
    type: 'Diagnostic Center',
    location: 'Multiple locations in Pune',
    minCost: 3000,
    maxCost: null,
    services: 'Home sample collection available',
    category: 'Premium',
    rating: 4.7,
    waitTime: 15,
    image: 'https://readdy.ai/api/search-image?query=modern-diagnostic-laboratory-center-advanced-medical-testing-facility-clean-professional-interior-high-tech-equipment-contemporary-design-bright-clinical-environment-medical-excellence-healthcare-technology&width=800&height=600&seq=redcliffe-labs-d006&orientation=landscape'
  },
  {
    id: 28,
    name: 'Apollo Diagnostics',
    type: 'Diagnostic Center',
    location: 'Multiple centers in Pune',
    minCost: 3500,
    maxCost: null,
    services: 'TORCH 10 profile (IgG and IgM)',
    category: 'Premium',
    rating: 4.8,
    waitTime: 20,
    image: 'https://readdy.ai/api/search-image?query=premium-diagnostic-center-building-apollo-medical-testing-facility-modern-architecture-professional-healthcare-center-clean-contemporary-design-advanced-laboratory-urban-location-bright-welcoming-entrance&width=800&height=600&seq=apollo-diagnostics-d007&orientation=landscape'
  },
  {
    id: 29,
    name: 'Nidan Diagnostic Services',
    type: 'Diagnostic Center',
    location: 'Wakad, Pimpri Chinchwad, Pune',
    minCost: 2500,
    maxCost: null,
    services: 'Modern diagnostic facilities',
    category: 'Mid-Range',
    rating: 4.5,
    waitTime: 18,
    image: 'https://readdy.ai/api/search-image?query=modern-diagnostic-services-center-medical-testing-facility-contemporary-architecture-professional-healthcare-building-clean-exterior-advanced-laboratory-urban-setting-bright-natural-lighting-accessible-care&width=800&height=600&seq=nidan-diagnostic-d008&orientation=landscape'
  },
  {
    id: 30,
    name: 'Lifeberries Health',
    type: 'Diagnostic Center',
    location: 'Pune',
    minCost: 4000,
    maxCost: 5500,
    services: 'Comprehensive diagnostic services',
    category: 'Premium',
    rating: 4.9,
    waitTime: 25,
    image: 'https://readdy.ai/api/search-image?query=premium-health-diagnostic-center-comprehensive-medical-testing-facility-modern-architecture-professional-healthcare-center-clean-contemporary-design-advanced-technology-urban-location-bright-welcoming-environment&width=800&height=600&seq=lifeberries-health-d009&orientation=landscape'
  },
  {
    id: 31,
    name: 'NM Medical',
    type: 'Diagnostic Center',
    location: 'Kalyani Nagar, Pune',
    minCost: 1300,
    maxCost: null,
    services: 'Advanced imaging and diagnostic services',
    category: 'Affordable',
    rating: 4.4,
    waitTime: 15,
    image: 'https://readdy.ai/api/search-image?query=affordable-medical-diagnostic-center-imaging-facility-modern-healthcare-building-professional-medical-center-clean-architecture-accessible-services-urban-location-bright-natural-lighting-welcoming-entrance&width=800&height=600&seq=nm-medical-d010&orientation=landscape'
  },
  {
    id: 32,
    name: 'PH Diagnostic Center',
    type: 'Diagnostic Center',
    location: 'Opposite Hotel Panchami, Satara Road, Pune',
    minCost: 2000,
    maxCost: 3800,
    services: 'State-of-the-art diagnostic facilities',
    category: 'Mid-Range',
    rating: 4.6,
    waitTime: 20,
    phone: '09240222222',
    image: 'https://readdy.ai/api/search-image?query=state-of-the-art-diagnostic-center-advanced-medical-testing-facility-modern-architecture-professional-healthcare-building-clean-contemporary-design-high-tech-equipment-urban-location-bright-welcoming-environment&width=800&height=600&seq=ph-diagnostic-d011&orientation=landscape'
  },
  {
    id: 33,
    name: 'Parvati Diagnostic Center',
    type: 'Diagnostic Center',
    location: 'Pune',
    minCost: 1250,
    maxCost: null,
    services: 'Precise and affordable diagnostic services',
    category: 'Affordable',
    rating: 4.3,
    waitTime: 18,
    image: 'https://readdy.ai/api/search-image?query=affordable-diagnostic-center-building-precise-medical-testing-facility-clean-professional-architecture-accessible-healthcare-services-modern-design-urban-location-bright-natural-lighting-welcoming-entrance&width=800&height=600&seq=parvati-diagnostic-d012&orientation=landscape'
  }
];

export default function HospitalExplorer() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: filtersRef, isVisible: filtersVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();

  const filters = ['All', 'Hospital', 'Diagnostic Center', 'Affordable', 'Mid-Range', 'Premium'];

  const filteredFacilities = facilities.filter((facility) => {
    const matchesFilter = activeFilter === 'All' || 
                         facility.category === activeFilter || 
                         facility.type === activeFilter;
    const matchesSearch = facility.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         facility.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Show only 3 facilities initially, or all if showAll is true
  const displayedFacilities = showAll ? filteredFacilities : filteredFacilities.slice(0, 3);
  const hasMore = filteredFacilities.length > 3;

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setShowAll(false); // Reset to show only 3 when filter changes
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Affordable':
        return 'bg-emerald-100 text-emerald-700';
      case 'Mid-Range':
        return 'bg-amber-100 text-amber-700';
      case 'Premium':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'Hospital' ? 'ri-hospital-line' : 'ri-test-tube-line';
  };

  // Calculate stats
  const totalHospitals = facilities.filter(f => f.type === 'Hospital').length;
  const totalDiagnosticCenters = facilities.filter(f => f.type === 'Diagnostic Center').length;

  return (
    <section id="hospitals" className="py-12 sm:py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-8 sm:mb-10 md:mb-12 ${headerVisible ? 'animate-fade-in-down' : 'opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-teal-100 rounded-full mb-3 sm:mb-4">
            <i className="ri-hospital-line text-teal-600 text-sm sm:text-base"></i>
            <span className="text-xs sm:text-sm font-semibold text-teal-700">Healthcare Facilities</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">Find the Right Care</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Compare hospitals and diagnostic centers based on your needs, budget, and location
          </p>
        </div>

        {/* Search and Filters */}
        <div
          ref={filtersRef}
          className={`mb-6 sm:mb-8 ${filtersVisible ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}
        >
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
            <div className="relative">
              <i className="ri-search-line absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-xl"></i>
              <input
                type="text"
                placeholder="Search by hospital name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:outline-none focus:border-teal-500 transition-colors"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => handleFilterChange(filter)}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                  activeFilter === filter
                    ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 sm:mb-6 text-center">
          <p className="text-sm sm:text-base text-gray-600">
            Showing <strong className="text-teal-600">{displayedFacilities.length}</strong> of <strong className="text-teal-600">{filteredFacilities.length}</strong> {filteredFacilities.length === 1 ? 'facility' : 'facilities'}
          </p>
        </div>

        {/* Facilities Grid */}
        <div
          ref={cardsRef}
          className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 ${cardsVisible ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}
        >
          {displayedFacilities.map((facility, index) => (
            <div
              key={facility.id}
              className="group bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative h-48 sm:h-56 w-full overflow-hidden">
                <img
                  src={facility.image}
                  alt={facility.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4 flex flex-wrap gap-1.5 sm:gap-2">
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${getCategoryColor(facility.category)}`}>
                    {facility.category}
                  </span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold bg-white/90 text-gray-700 backdrop-blur-sm">
                    <i className={`${getTypeIcon(facility.type)} mr-1`}></i>
                    {facility.type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                {/* Title and Rating */}
                <div className="mb-3">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {facility.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-${i < Math.floor(facility.rating) ? 'fill' : 'line'} text-amber-400 text-xs sm:text-sm`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">{facility.rating}</span>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-2 mb-3 sm:mb-4">
                  <i className="ri-map-pin-line text-teal-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{facility.location}</p>
                </div>

                {/* Bed Count and Doctors (for hospitals) */}
                {facility.bedCount && (
                  <div className="flex gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                      <i className="ri-hotel-bed-line text-teal-600"></i>
                      <span>{facility.bedCount} beds</span>
                    </div>
                    {facility.doctors && (
                      <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                        <i className="ri-user-heart-line text-teal-600"></i>
                        <span>{facility.doctors} doctors</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cost */}
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-teal-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-gray-600">Cost Range</span>
                    <div className="text-right">
                      <p className="text-base sm:text-lg font-bold text-teal-700">
                        ₹{facility.minCost.toLocaleString()}
                        {facility.maxCost && ` - ₹${facility.maxCost.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="mb-3 sm:mb-4">
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                    <i className="ri-service-line text-teal-600 mr-1"></i>
                    {facility.services}
                  </p>
                </div>

                {/* Wait Time */}
                <div className="flex items-center gap-2 mb-4 sm:mb-6 text-xs sm:text-sm text-gray-600">
                  <i className="ri-time-line text-teal-600"></i>
                  <span>Avg. Wait: <strong>{facility.waitTime} mins</strong></span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 sm:gap-3">
                  <button 
                    onClick={() => setSelectedFacility(facility)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-teal-600 text-teal-600 rounded-xl font-semibold text-xs sm:text-sm hover:bg-teal-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-information-line mr-1 sm:mr-2"></i>
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* See All Button */}
        {hasMore && !showAll && (
          <div className="mt-8 sm:mt-12 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-teal-600 text-white rounded-full font-semibold text-sm sm:text-base hover:bg-teal-700 transition-all hover:shadow-lg shadow-teal-600/30 whitespace-nowrap inline-flex items-center gap-2"
            >
              <span>See All {filteredFacilities.length} Facilities</span>
              <i className="ri-arrow-down-line text-lg sm:text-xl"></i>
            </button>
          </div>
        )}

        {/* Show Less Button */}
        {showAll && hasMore && (
          <div className="mt-8 sm:mt-12 text-center">
            <button
              onClick={() => {
                setShowAll(false);
                document.getElementById('hospitals')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-teal-600 border-2 border-teal-600 rounded-full font-semibold text-sm sm:text-base hover:bg-teal-50 transition-all whitespace-nowrap inline-flex items-center gap-2"
            >
              <span>Show Less</span>
              <i className="ri-arrow-up-line text-lg sm:text-xl"></i>
            </button>
          </div>
        )}

        {/* No Results */}
        {filteredFacilities.length === 0 && (
          <div className="text-center py-12 sm:py-16">
            <i className="ri-search-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No facilities found</h3>
            <p className="text-sm sm:text-base text-gray-600">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Stats Section */}
        <div className="mt-12 sm:mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-xl mx-auto mb-2 sm:mb-4">
              <i className="ri-hospital-line text-2xl sm:text-3xl text-teal-600"></i>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">33</div>
            <div className="text-xs sm:text-sm text-gray-600">Total Facilities</div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-purple-100 rounded-xl mx-auto mb-2 sm:mb-4">
              <i className="ri-building-line text-2xl sm:text-3xl text-purple-600"></i>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{totalHospitals}</div>
            <div className="text-xs sm:text-sm text-gray-600">Hospitals</div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-2 sm:mb-4">
              <i className="ri-test-tube-line text-2xl sm:text-3xl text-amber-600"></i>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{totalDiagnosticCenters}</div>
            <div className="text-xs sm:text-sm text-gray-600">Diagnostic Centers</div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg col-span-2 md:col-span-1">
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-emerald-100 rounded-xl mx-auto mb-2 sm:mb-4">
              <i className="ri-money-rupee-circle-line text-2xl sm:text-3xl text-emerald-600"></i>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">₹800+</div>
            <div className="text-xs sm:text-sm text-gray-600">Starting From</div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedFacility && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
          onClick={() => setSelectedFacility(null)}
        >
          <div 
            className="bg-white rounded-2xl sm:rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Image */}
            <div className="relative h-48 sm:h-64 w-full">
              <img
                src={selectedFacility.image}
                alt={selectedFacility.name}
                className="w-full h-full object-cover object-top"
              />
              <button
                onClick={() => setSelectedFacility(null)}
                className="absolute top-3 sm:top-4 right-3 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-lg sm:text-xl text-gray-700"></i>
              </button>
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 flex flex-wrap gap-1.5 sm:gap-2">
                <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${getCategoryColor(selectedFacility.category)}`}>
                  {selectedFacility.category}
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold bg-white/90 text-gray-700 backdrop-blur-sm">
                  <i className={`${getTypeIcon(selectedFacility.type)} mr-1`}></i>
                  {selectedFacility.type}
                </span>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-5 sm:p-6 md:p-8">
              {/* Title and Rating */}
              <div className="mb-4 sm:mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {selectedFacility.name}
                </h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-${i < Math.floor(selectedFacility.rating) ? 'fill' : 'line'} text-amber-400 text-base sm:text-lg`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-base sm:text-lg font-semibold text-gray-700">{selectedFacility.rating}</span>
                  <span className="text-sm sm:text-base text-gray-500">rating</span>
                </div>
              </div>

              {/* Location */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-xl">
                <div className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-map-pin-line text-teal-600 text-xl sm:text-2xl mt-1"></i>
                  <div>
                    <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">Location</div>
                    <p className="text-sm sm:text-base text-gray-700">{selectedFacility.location}</p>
                  </div>
                </div>
              </div>

              {/* Bed Count and Doctors (for hospitals) */}
              {selectedFacility.bedCount && (
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="p-3 sm:p-4 bg-teal-50 rounded-xl">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                      <i className="ri-hotel-bed-line text-teal-600 text-lg sm:text-xl"></i>
                      <span className="text-xs sm:text-sm font-semibold text-gray-900">Bed Capacity</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-teal-700">{selectedFacility.bedCount}</p>
                  </div>
                  {selectedFacility.doctors && (
                    <div className="p-3 sm:p-4 bg-purple-50 rounded-xl">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                        <i className="ri-user-heart-line text-purple-600 text-lg sm:text-xl"></i>
                        <span className="text-xs sm:text-sm font-semibold text-gray-900">Doctors</span>
                      </div>
                      <p className="text-xl sm:text-2xl font-bold text-purple-700">{selectedFacility.doctors}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Cost */}
              <div className="mb-4 sm:mb-6 p-4 sm:p-6 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border-2 border-teal-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">Cost Range</div>
                    <p className="text-2xl sm:text-3xl font-bold text-teal-700">
                      ₹{selectedFacility.minCost.toLocaleString()}
                      {selectedFacility.maxCost && ` - ₹${selectedFacility.maxCost.toLocaleString()}`}
                    </p>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-money-rupee-circle-line text-xl sm:text-2xl text-white"></i>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <i className="ri-service-line text-teal-600 text-lg sm:text-xl"></i>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900">Services Offered</h4>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {selectedFacility.services}
                </p>
              </div>

              {/* Wait Time */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-blue-600 text-lg sm:text-xl"></i>
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">Average Wait Time</span>
                  </div>
                  <span className="text-lg sm:text-xl font-bold text-blue-700">{selectedFacility.waitTime} minutes</span>
                </div>
              </div>

              {/* Insurance & Coverage Section */}
              <InsuranceSection />

              {/* Close Button */}
              <button
                onClick={() => setSelectedFacility(null)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-teal-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
